import { useCallback, useEffect, useRef, useState } from 'react';
import {
  deletePersonal,
  fetchPersonalList,
  patchPersonal,
} from '../../lib/api.js';
import { Icon } from '../Icon.js';
import type { PersonalListItem } from '../../lib/types.js';

const PAGE_SIZE = 20;
type Familiarity = 0 | 1 | 2 | 3 | 4 | 5;
const ALL_FAMS: Familiarity[] = [0, 1, 2, 3, 4, 5];

export function PersonalVocabList(): JSX.Element {
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [famFilter, setFamFilter] = useState<Familiarity[]>([]);
  const [items, setItems] = useState<PersonalListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openWord, setOpenWord] = useState<string | null>(null);
  const reqIdRef = useRef(0);

  // Debounce search input → 200 ms.
  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedQuery(query.trim()), 200);
    return () => window.clearTimeout(t);
  }, [query]);

  const load = useCallback(
    async (offset: number, replace: boolean) => {
      const myReq = ++reqIdRef.current;
      setLoading(true);
      setError(null);
      try {
        const res = await fetchPersonalList({
          q: debouncedQuery || undefined,
          familiarity: famFilter.length > 0 ? famFilter : undefined,
          sort: 'recent',
          limit: PAGE_SIZE,
          offset,
        });
        if (myReq !== reqIdRef.current) return;
        setTotal(res.total);
        setItems((prev) => (replace ? res.items : [...prev, ...res.items]));
      } catch (e) {
        if (myReq !== reqIdRef.current) return;
        setError((e as Error).message ?? '加载失败');
      } finally {
        if (myReq === reqIdRef.current) setLoading(false);
      }
    },
    [debouncedQuery, famFilter],
  );

  useEffect(() => {
    void load(0, true);
  }, [load]);

  const toggleFam = (f: Familiarity) => {
    setFamFilter((prev) =>
      prev.includes(f) ? prev.filter((x) => x !== f) : [...prev, f],
    );
  };

  const handleEdit = async (word: string, fam: Familiarity) => {
    try {
      await patchPersonal(word, { familiarity: fam });
      setItems((prev) =>
        prev.map((it) => (it.word === word ? { ...it, familiarity: fam } : it)),
      );
    } catch (e) {
      setError((e as Error).message ?? '更新失败');
    }
  };

  const handleDelete = async (word: string) => {
    if (!window.confirm(`确认删除「${word}」?`)) return;
    try {
      await deletePersonal(word);
      setItems((prev) => prev.filter((it) => it.word !== word));
      setTotal((t) => Math.max(0, t - 1));
      setOpenWord(null);
    } catch (e) {
      setError((e as Error).message ?? '删除失败');
    }
  };

  const hasMore = items.length < total;

  return (
    <section aria-label="个人词库">
      <h2 className="text-secondary text-hint mb-2">个人词库</h2>
      <div className="flex flex-col gap-3">
        <label className="flex items-center gap-3 rounded-2xl bg-surface border border-border px-4 h-11">
          <Icon name="search" size={18} className="text-secondary shrink-0" />
          <input
            type="search"
            inputMode="search"
            autoComplete="off"
            spellCheck={false}
            placeholder="搜索词条"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="flex-1 bg-transparent text-primary placeholder:text-muted text-sentence"
          />
        </label>

        <div
          role="group"
          aria-label="熟悉度过滤"
          className="flex flex-wrap gap-2"
        >
          {ALL_FAMS.map((f) => {
            const active = famFilter.includes(f);
            return (
              <button
                key={f}
                type="button"
                aria-pressed={active}
                onClick={() => toggleFam(f)}
                className={[
                  'h-8 min-w-[2.25rem] px-3 rounded-full border text-hint',
                  active
                    ? 'bg-primary text-canvas border-primary'
                    : 'bg-surface text-secondary border-border active:bg-surface-2',
                ].join(' ')}
              >
                {f}
              </button>
            );
          })}
        </div>

        <ul className="flex flex-col bg-surface border border-border rounded-2xl divide-y divide-border">
          {items.length === 0 && !loading ? (
            <li className="px-4 py-6 text-secondary text-hint text-center">
              {error ? `加载失败 · ${error}` : '暂无词条'}
            </li>
          ) : (
            items.map((it) => (
              <li key={it.word}>
                <Row
                  item={it}
                  open={openWord === it.word}
                  onToggle={() =>
                    setOpenWord((cur) => (cur === it.word ? null : it.word))
                  }
                  onEdit={(f) => void handleEdit(it.word, f)}
                  onDelete={() => void handleDelete(it.word)}
                />
              </li>
            ))
          )}
        </ul>

        {error && items.length > 0 ? (
          <div className="text-secondary text-hint break-words">操作失败 · {error}</div>
        ) : null}

        <div className="flex items-center justify-between text-secondary text-hint">
          <span>
            {items.length} / {total}
          </span>
          {hasMore ? (
            <button
              type="button"
              onClick={() => void load(items.length, false)}
              disabled={loading}
              className="px-4 h-9 rounded-full border border-border text-primary active:bg-surface-2 disabled:opacity-50"
            >
              {loading ? '载入中...' : '加载更多'}
            </button>
          ) : loading ? (
            <span>载入中...</span>
          ) : null}
        </div>
      </div>
    </section>
  );
}

type RowProps = {
  item: PersonalListItem;
  open: boolean;
  onToggle: () => void;
  onEdit: (f: Familiarity) => void;
  onDelete: () => void;
};

function Row({ item, open, onToggle, onEdit, onDelete }: RowProps): JSX.Element {
  return (
    <div className="flex flex-col">
      <button
        type="button"
        onClick={onToggle}
        className="w-full text-left px-4 py-3 active:bg-surface-2"
      >
        <div className="flex items-center gap-3">
          <div className="flex-1 min-w-0 flex flex-col">
            <div className="flex items-baseline gap-2 min-w-0">
              <span className="text-primary text-word-sm truncate">{item.word}</span>
              {item.phonetic ? (
                <span className="text-secondary text-ipa truncate">/{item.phonetic}/</span>
              ) : null}
            </div>
            {item.translation_zh ? (
              <span className="text-secondary text-hint truncate">{item.translation_zh}</span>
            ) : null}
          </div>
          <FamBadge value={item.familiarity} />
          {item.is_due_now ? (
            <span className="shrink-0 text-muted text-hint border border-border rounded-full px-2 py-0.5">
              今日到期
            </span>
          ) : null}
        </div>
      </button>
      {open ? (
        <div className="px-4 pb-4 pt-1 flex flex-col gap-3 bg-surface-2">
          <div className="flex items-center gap-2">
            <span className="text-secondary text-hint shrink-0">熟悉度</span>
            <div className="flex-1 grid grid-cols-6 gap-1">
              {ALL_FAMS.map((f) => {
                const active = item.familiarity === f;
                return (
                  <button
                    key={f}
                    type="button"
                    onClick={() => onEdit(f)}
                    className={[
                      'h-9 rounded-full text-hint border',
                      active
                        ? 'bg-primary text-canvas border-primary'
                        : 'bg-surface text-secondary border-border active:bg-surface-2',
                    ].join(' ')}
                  >
                    {f}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => onEdit(0)}
              className="flex-1 h-10 rounded-full border border-border text-primary active:bg-surface text-hint"
            >
              降级到 0
            </button>
            <button
              type="button"
              onClick={onDelete}
              className="flex-1 h-10 rounded-full border border-border bg-surface text-primary active:bg-surface-2 text-hint"
            >
              删除
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function FamBadge({ value }: { value: Familiarity }): JSX.Element {
  return (
    <span className="shrink-0 h-7 min-w-[1.75rem] px-2 rounded-full bg-surface-2 border border-border text-primary text-hint inline-flex items-center justify-center tabular-nums">
      {value}
    </span>
  );
}
